import React, { useRef, FormEvent, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Settings } from 'lucide-react';
import { isDevelopment } from '@/utils/environment';
import { DEV_ACCOUNTS } from '../constants';
import Logo from '@/assets/images/logo.png';

/**
 * LoginForm component - Handles user authentication
 */
const LoginForm: React.FC = () => {
  const { login, loading } = useAuth();
  const inputEmail = useRef<HTMLInputElement>(null);
  const inputPassword = useRef<HTMLInputElement>(null);
  const [devLoading, setDevLoading] = useState(false);

  // Verificar si estamos en modo desarrollo
  const isDevMode = isDevelopment();

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    login({
      email: inputEmail.current?.value || '',
      password: inputPassword.current?.value || ''
    });
  };

  const handleDevLogin = async (account: typeof DEV_ACCOUNTS[0]) => {
    setDevLoading(true);
    try {
      await login({
        email: account.email,
        password: account.password
      });
    } catch (error) {
      console.error('Error en login de desarrollo:', error);
    } finally {
      setDevLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen relative">
      <div className="w-full lg:w-1/3 p-4">
        <div className="flex justify-center my-12">
          <img src={Logo} alt="Logo" />
        </div>
        <form onSubmit={handleSubmit}>
          <div className="my-4">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              ref={inputEmail}
              type="email"
              autoComplete="username"
              placeholder="Ingrese su correo"
              disabled={loading || devLoading}
            />
          </div>

          <div className="my-4">
            <Label htmlFor="password">Contraseña</Label>
            <Input
              id="password"
              ref={inputPassword}
              type="password"
              autoComplete="current-password"
              placeholder="Ingresar contraseña"
              disabled={loading || devLoading}
            />
          </div>
          <div className="flex justify-center my-12">
            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={loading || devLoading}
            >
              {(loading || devLoading) ? 'Ingresando...' : 'Ingresar'}
            </Button>
          </div>
        </form>
      </div>

      {/* Botón flotante para desarrollo */}
      {isDevMode && (
        <div className="fixed bottom-6 right-6 z-50">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="secondary"
                size="lg"
                className="rounded-full shadow-lg hover:shadow-xl transition-shadow bg-orange-500 hover:bg-orange-600 text-white border-0"
                disabled={loading || devLoading}
                data-testid="dev-login-button"
              >
                <Settings className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel className="text-center">
                Login Desarrollo
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {DEV_ACCOUNTS.map((account) => {
                const IconComponent = account.icon;
                return (
                  <DropdownMenuItem
                    key={account.role}
                    onClick={() => handleDevLogin(account)}
                    className="cursor-pointer flex items-center gap-2"
                    disabled={loading || devLoading}
                  >
                    <IconComponent className="h-4 w-4" />
                    <span>{account.label}</span>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
};

export default LoginForm;